import { useFormik } from "formik";
import type { NextPageWithLayout } from "next";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { accountApi } from "@/apis/accounts/accountApi";
import {
  Account,
  GoogleCalendarEventsInner,
  GoogleCalendarSettingDetail,
  GoogleCalendarSettingDetailListSettingsInner,
  GoogleCalendarSettingDetailListSettingsInnerCalendarImportIdsInner,
  PostGoogleCalendarSettingRequestListSettingsInner,
  ServiceType,
} from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
import serviceApi from "@/apis/service/serviceApi";
import { Button } from "@/components/atoms/buttons/Button";
import { ButtonText } from "@/components/atoms/buttons/ButtonText";
import OutlineButton from "@/components/atoms/buttons/OutlineButton";
import { Select } from "@/components/atoms/inputForms/Select";
import WarningIcon from "@/components/icons/WarningRedIcon";
import DeleteModal from "@/components/molecules/modals/DeleteModal";
import { PageHeader } from "@/components/organisms/header/PageHeader";
import { LayoutTemplate } from "@/components/templates/layout";
import { useSystemMessage } from "@/hooks/systemMessage";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { useLoading } from "@/states/loading/operations";
import { useUserAccount } from "@/states/auth/selectoers";
import ErrorLimitByPlanModal from "@/components/organisms/modals/ErrorLimitByPlanModal";

const EmptyCalendarEvent: GoogleCalendarSettingDetailListSettingsInnerCalendarImportIdsInner =
  {
    calendar_id: "",
    id: "",
  };

const defaultSettingItem: GoogleCalendarSettingDetailListSettingsInner = {
  service_id: "",
  calendar_import_ids: [EmptyCalendarEvent],
  calendar_export_ids: [EmptyCalendarEvent],
};

interface ServiceOptionProp {
  text: string;
  value: number;
  serviceType: ServiceType;
  disabled?: boolean;
}

interface DeleteSettingModalProp {
  isOpen: boolean;
  selectedSettingIndex: number | null;
}

interface AxiosResponseAccount {
  data: {
    data: Account
  }
}

const AccountSyncGoogleCalendarPage: NextPageWithLayout = () => {
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();
  const systemMessage = useSystemMessage();
  const account = useUserAccount();

  const [configDetail, setConfigDetail] =
    useState<GoogleCalendarSettingDetail>();
  const [serviceOptions, setServiceOptions] = useState<ServiceOptionProp[]>([]);
  const [eventList, setEventList] = useState<GoogleCalendarEventsInner[]>();

  const [
    isOpenDeleteConnectGoogleCalendarModal,
    setOpenDeleteConnectGoogleCalendarModal,
  ] = useState(false);

  const [deleteSettingModal, setDeleteSettingModal] =
    useState<DeleteSettingModalProp>({
      isOpen: false,
      selectedSettingIndex: null,
    });

  const allEventOption = useMemo(() => {
    return (
      eventList
        ?.filter((event) => event.isActive)
        ?.map((event) => ({
          text: event.calendar_name || "",
          value: event.id,
        })) || []
    );
  }, [eventList]);

  const formik = useFormik<{
    settings: GoogleCalendarSettingDetailListSettingsInner[];
  }>({
    initialValues: {
      settings: [defaultSettingItem],
    },
    onSubmit: (values) => {
      handleSubmitSettings(values);
    },
  });

  const fetchAllService = useCallback(() => {
    void withLoading(async () => {
      try {
        const res = await serviceApi.indexServiceName();
        if (res && res.data) {
          const _serviceOptions = res.data
            .filter(
              (item) =>
                item.type === ServiceType.EVENT_SEMINAR ||
                item.type === ServiceType.CALENDAR_RESERVATION,
            )
            .map((item) => ({
              value: item.id,
              text: item.name,
              serviceType: item.type,
            }));
          setServiceOptions(_serviceOptions);
        }
      } catch (e) {
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
          return;
        }
      }
    });
  }, [handlePageError, withLoading]);

  useEffect(() => {
    fetchAllService();
  }, [fetchAllService]);

  const getAccountSettingCalendar = (loadEventList?: boolean) => {
    void withLoading(async () => {
      const res = await accountApi.getCalendarSetting();

      setConfigDetail(res.data);
      const listSetting = res?.data?.list_settings;

      // Keep the content list when the link is broken; this used to reset it, so
      // settings looked lost after re-authorising (redmine #1144).
      void formik.setValues({
        settings: listSetting?.length
          ? listSetting.map((setting) => ({
              id: setting.id,
              service_id: setting.service_id,
              service_type: setting.service_type,
              calendar_import_ids:
                setting.calendar_import_ids &&
                setting.calendar_import_ids?.length > 0
                  ? setting.calendar_import_ids
                  : [EmptyCalendarEvent],
              calendar_export_ids:
                setting.calendar_export_ids &&
                setting.calendar_export_ids.length > 0
                  ? setting.calendar_export_ids
                  : [EmptyCalendarEvent],
            }))
          : [defaultSettingItem],
      });

      // Fetching the calendar list needs a valid token.
      if (loadEventList && res?.data?.is_connected) {
        const eventData = await accountApi.getGoogleCalendarEvent();
        if (eventData.data) {
          setEventList(
            eventData?.data.length
              ? eventData.data
              : [
                  {
                    calendar_id: "",
                    id: "",
                    isActive: true,
                  } as GoogleCalendarEventsInner,
                ],
          );
        }
      }
    });
  };

  const handleConnectGoogleCalendar = async () => {
    const res = await accountApi.getConnectGoogleCalendar();
    const url = res.data?.url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleCancelConnectGoogleCalendar = () => {
    void withLoading(async () => {
      try {
        await accountApi.cancelGoogleCalendarSetting();
        setConfigDetail(undefined);
        systemMessage.success("Googleカレンダーの接続が解除されました");
      } catch (error) {
        if (error instanceof ApiResponseError) {
          let errorMessage = "";
          error.errors?.forEach((err) => {
            errorMessage = err.message ?? errorMessage;
          });
          systemMessage.danger(errorMessage);
        }
      } finally {
        setOpenDeleteConnectGoogleCalendarModal(false);
      }
    });
  };

  const handleDeleteSettingGoogleCalendar = (settingIndex: number) => {
    void formik.setValues((prev) => {
      const filterResult =
        prev.settings.filter((_, index) => index !== settingIndex) || [];

      return {
        settings: [...filterResult],
      };
    });
    setDeleteSettingModal({
      isOpen: false,
      selectedSettingIndex: null,
    });
  };

  const addNewSetting = () => {
    void formik.setValues((prev) => {
      return {
        settings: [...prev.settings, defaultSettingItem],
      };
    });
  };
  const getUpdatedServiceOptions = (existIds: number[]) => {
    return serviceOptions.map((option) => ({
      ...option,
      disabled: existIds.includes(option.value),
    }));
  };

  const getUpdatedEventOptions = (existIds: string[]) => {
    return allEventOption.map((option) => ({
      ...option,
      disabled: existIds.includes(option.value),
    }));
  };

  const handleSubmitSettings = ({
    settings,
  }: {
    settings: GoogleCalendarSettingDetailListSettingsInner[];
  }) => {
    void withLoading(async () => {
      try {
        const payload = settings
          .filter((item) => !!item.service_id)
          .map<PostGoogleCalendarSettingRequestListSettingsInner>((item) => ({
            id: item.id,
            service_id: item.service_id || "",
            calendar_import_ids:
              item.calendar_import_ids
                ?.filter((importItem) => importItem.calendar_id)
                .map((importItem) => importItem?.calendar_id) || [],
            calendar_export_ids:
              item.calendar_export_ids
                ?.filter((importItem) => importItem.calendar_id)
                .map((importItem) => importItem?.calendar_id) || [],
          }));
        await accountApi.postGoogleCalendarSetting({
          postGoogleCalendarSettingRequest: { list_settings: payload },
        });
        systemMessage.success("更新されました");
        void getAccountSettingCalendar();
      } catch (error) {
        systemMessage.danger("入力に誤りがあります");
      }
    });
  };

  useEffect(() => {
    void getAccountSettingCalendar(true);
  }, []);

  useEffect(() => {
    if (configDetail && configDetail.id) {
      const listSettings = configDetail.list_settings.length
        ? configDetail.list_settings
        : [defaultSettingItem];

      void formik.setFieldValue("", listSettings);
    }
  }, [configDetail?.id, configDetail, formik]);

  const [isPopupVisible, setIsPopupVisible] = useState(!account?.user_plan_config?.google_calendar);
  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };
  if (!account?.user_plan_config?.google_calendar) {
    return (
      <>
        {
          isPopupVisible && <ErrorLimitByPlanModal onClose={handleClosePopup} reason="GOOGLE_CALENDAR_SYNC" />
        }
      </>
    );
  }

  return (
    <div className="mb-[60px]">
      {isOpenDeleteConnectGoogleCalendarModal && (
        <DeleteModal
          title="Googleアカウント連携解除"
          isOpen={isOpenDeleteConnectGoogleCalendarModal}
          handleClose={() => setOpenDeleteConnectGoogleCalendarModal(false)}
          handleSubmit={handleCancelConnectGoogleCalendar}
          btnText="解除"
        >
          <p>Googleアカウントの連携を解除すると、下記が起こります。</p>
          <div className="pl-[20px]">
            <ul className="list-disc list-outside">
              <li>今後新しく入った予約はGoogleカレンダーに反映されません。</li>
              <li>Google カレンダーとの連携はすべて解除されます。</li>
            </ul>
          </div>
          <p className="mt-4">Googleアカウント連携を解除しますか？</p>
        </DeleteModal>
      )}
      {deleteSettingModal.isOpen && (
        <DeleteModal
          title="対象のコンテンツ解除"
          isOpen={deleteSettingModal.isOpen}
          handleClose={() =>
            setDeleteSettingModal({
              isOpen: false,
              selectedSettingIndex: null,
            })
          }
          handleSubmit={() => {
            if (deleteSettingModal.selectedSettingIndex !== null) {
              handleDeleteSettingGoogleCalendar(
                deleteSettingModal.selectedSettingIndex,
              );
            }
          }}
          btnText="解除"
        >
          <p>対象のコンテンツ解除すると、下記が起こります。</p>
          <div className="pl-[20px]">
            <ul className="list-disc list-outside">
              <li>今後新しく入った予約はGoogleカレンダーに反映されません。</li>
              <li>Google カレンダーとの対象のコンテンツ連携は解除されます。</li>
            </ul>
          </div>

          <p className="mt-4">対象のコンテンツ解除しますか？</p>
        </DeleteModal>
      )}
      <article>
        <div className="md:mt-6 mb-10">
          <PageHeader title="Googleカレンダー連携"></PageHeader>
        </div>

        {configDetail?.needs_reauth && (
          <div className="flex flex-col gap-4 items-start p-4 mb-10 rounded-[4px] border border-[#D32932] bg-[#FDECEC] md:flex-row md:items-center">
            <WarningIcon width={20} height={20} className="shrink-0 mt-1" />
            <div className="flex-1">
              <p className="font-bold text-[#D32932]">
                Googleカレンダーとの連携が解除されています
              </p>
              <p className="text-[14px]">
                現在、Googleカレンダーの予定がヨヤクーに反映されていません。予約の重複を防ぐため、再連携をお願いします。
              </p>
            </div>
            <Button
              label={"再連携する"}
              className="shrink-0 !w-[193px] !h-[36px] text-[14px]"
              onClick={handleConnectGoogleCalendar}
            />
          </div>
        )}

        <div className="mb-10 space-y-4 rounded-[4px] border border-gray-100">
          <div className="px-4 py-6">
            <h2 className="text-[20px] font-bold">
              Googleカレンダー連携でできること
            </h2>
          </div>
          <div className="px-4 py-6 border-t border-gray-100">
            <div className="mb-6">
              <p className="text-[#222222] font-bold mb-1">
                Googleカレンダーにすべての予約を表示
              </p>
              <p>予約・申込情報を自動でGoogleカレンダーに表示します。</p>
            </div>
            <div className="flex items-center bg-[#F7F8F9] p-4 ">
              {configDetail?.needs_reauth ? (
                <div>
                  <p className="font-bold">現在連携中のアカウント</p>
                  <p className="text-[#D32932]">連携されていません</p>
                </div>
              ) : configDetail?.gcalendar_email ? (
                <>
                  <div>
                    <p className="font-bold">現在連携中のアカウント</p>
                    <p>{configDetail?.gcalendar_email}</p>
                  </div>
                  <ButtonText
                    label="アカウント連携を解除する"
                    className="ml-auto !w-[193px]"
                    labelClassNameContainer="text-[#D32932] !border-0"
                    onClick={() => {
                      setOpenDeleteConnectGoogleCalendarModal(true);
                    }}
                  />
                </>
              ) : (
                <div className="flex flex-col md:flex-row">
                  <p>
                    Googleカレンダーを設定するには、Googleアカウントを連携してください。
                  </p>
                  <Button
                    label={"Googleアカウントと連携"}
                    className="ml-auto !w-[193px] !h-[36px] text-[14px]"
                    onClick={handleConnectGoogleCalendar}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {configDetail?.gcalendar_email && (
          <div>
            <p className="text-[20px] font-bold mb-3 text-[#1A1A1A]">
              インポート・エクスポート先のカレンダー設定
            </p>
            <p className="text-[16px] text-[#626262] mb-2">
              インポート：Googleカレンダーの予定をyoyacooに取り込むことができます。
            </p>
            <p className="text-[16px] text-[#626262] mb-2">
              エクスポート：yoyacooで決まった予定をGoogleカレンダーに自動登録することができます。
            </p>
            <p className="text-[16px] text-[#626262] mb-2" style={{marginLeft: "115px"}}>
            ※書き込み権限がないカレンダーは連携ができません。
            </p>
            <div className="flex flex-col mt-3 mb-4">
              {formik.values.settings &&
                formik.values.settings.length > 0 &&
                formik.values.settings.map((setting, settingIndex) => {
                  const serviceIds = formik.values.settings
                    .filter(
                      (setting) => setting.service_id && setting.service_id,
                    )
                    .map((setting) => Number(setting.service_id));
                  const filerServiceOptions =
                    getUpdatedServiceOptions(serviceIds);
                  const isDisabledImportSetting =
                    setting.service_type === ServiceType.EVENT_SEMINAR;
                  return (
                    <div
                      key={settingIndex}
                      className="px-4 py-6 rounded-[4px] border border-gray-100 mb-4"
                    >
                      <div className="w-full">
                        <p className="text-[#626262] font-bold text-[16px] mb-2">
                          対象のコンテンツ
                        </p>
                        <div className="flex">
                          <Select
                            className="w-[calc(100%-48px)]"
                            hasSearchInput
                            onChange={(value) => {
                              const selectedItem = filerServiceOptions.filter(
                                (item) => Number(item.value) === Number(value),
                              );
                              let newItem = {
                                ...setting,
                                service_id: value,
                                service_type: selectedItem
                                  ? selectedItem[0].serviceType
                                  : undefined,
                              };

                              if (
                                selectedItem &&
                                selectedItem[0].serviceType ===
                                  ServiceType.EVENT_SEMINAR
                              ) {
                                newItem = {
                                  ...newItem,
                                  calendar_import_ids: [{ calendar_id: "" }],
                                };
                              }

                              void formik.setValues((prev) => {
                                const newSettings = [...prev.settings];
                                newSettings[settingIndex] = newItem;

                                return {
                                  settings: newSettings,
                                };
                              });
                            }}
                            value={setting.service_id}
                            options={filerServiceOptions}
                            placeholder="選択してください"
                          />
                          <ButtonText
                            labelClassNameContainer="text-[#D32932] !border-0"
                            label="削除"
                            onClick={() => {
                              setDeleteSettingModal({
                                isOpen: true,
                                selectedSettingIndex: settingIndex,
                              });
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex mt-6">
                        <div className="w-1/2 pr-6">
                          <p className="text-[#626262] font-bold text-[16px] mb-1">
                            インポートするカレンダー
                          </p>
                          <div className="mt-2 min-h-[52px]">
                            {!isDisabledImportSetting &&
                              setting.calendar_import_ids?.map(
                                (exportId, importIndex) => {
                                  const exitedIds =
                                    setting.calendar_import_ids
                                      ?.filter((item) => item.calendar_id)
                                      .map((item) => item.calendar_id) || [];
                                  const options =
                                    getUpdatedEventOptions(exitedIds);
                                  return (
                                    <div
                                      className="flex mb-2"
                                      key={`import-setting-${importIndex}`}
                                    >
                                      <Select
                                        className="w-[calc(100%-48px)]"
                                        onChange={(value) => {
                                          void formik.setValues((prev) => {
                                            const newSettings = [
                                              ...prev.settings,
                                            ];
                                            const calendarImportIds = [
                                              ...(newSettings[settingIndex]
                                                .calendar_import_ids || []),
                                            ];
                                            calendarImportIds[importIndex] = {
                                              calendar_id: value,
                                            };
                                            newSettings[settingIndex] = {
                                              ...newSettings[settingIndex],
                                              calendar_import_ids:
                                                calendarImportIds,
                                            };

                                            return {
                                              settings: newSettings,
                                            };
                                          });
                                        }}
                                        value={exportId.calendar_id}
                                        options={options}
                                        placeholder="選択してください"
                                      />

                                      <ButtonText
                                        labelClassNameContainer="text-[#D32932] !border-0"
                                        label="削除"
                                        onClick={() => {
                                          void formik.setValues((prev) => {
                                            const newSettings = [
                                              ...prev.settings,
                                            ];
                                            let calendarImportIds = newSettings[
                                              settingIndex
                                            ].calendar_import_ids?.filter(
                                              (item, index) =>
                                                index !== importIndex,
                                            );
                                            if (
                                              calendarImportIds?.length === 0
                                            ) {
                                              calendarImportIds = [
                                                EmptyCalendarEvent,
                                              ];
                                            }
                                            newSettings[settingIndex] = {
                                              ...newSettings[settingIndex],
                                              calendar_import_ids:
                                                calendarImportIds,
                                            };

                                            return {
                                              settings: newSettings,
                                            };
                                          });
                                        }}
                                      />
                                    </div>
                                  );
                                },
                              )}
                          </div>
                          <OutlineButton
                            type="button"
                            color="primary"
                            onClick={() => {
                              void formik.setValues((prev) => {
                                const newSettings = [...prev.settings];
                                const calendarExportIds = [
                                  ...(newSettings[settingIndex]
                                    .calendar_import_ids || []),
                                  { calendar_id: "" },
                                ];
                                newSettings[settingIndex] = {
                                  ...newSettings[settingIndex],
                                  calendar_import_ids: calendarExportIds,
                                };

                                return {
                                  settings: newSettings,
                                };
                              });
                            }}
                            disabled={isDisabledImportSetting}
                            customClass="text-base !font-bold leading-normal"
                          >
                            追加
                          </OutlineButton>
                        </div>

                        <div className="w-1/2 border-l border-[#DBE0E6] pl-6">
                          <p className="text-[#626262] font-bold text-[16px] mb-1">
                            エクスポートするカレンダー
                          </p>
                          <div className="mt-2 min-h-[52px]">
                            {setting.calendar_export_ids?.map(
                              (exportId, exportIndex) => {
                                const exitedIds =
                                  setting.calendar_export_ids
                                    ?.filter((item) => item.calendar_id)
                                    .map((item) => item.calendar_id) || [];
                                const options =
                                  getUpdatedEventOptions(exitedIds);
                                return (
                                  <div
                                    className="flex mb-2"
                                    key={`export-setting-${exportIndex}`}
                                  >
                                    <Select
                                      className="w-[calc(100%-48px)]"
                                      onChange={(value) => {
                                        void formik.setValues((prev) => {
                                          const newSettings = [
                                            ...prev.settings,
                                          ];
                                          const calendarExportIds = [
                                            ...(newSettings[settingIndex]
                                              .calendar_export_ids || []),
                                          ];
                                          calendarExportIds[exportIndex] = {
                                            calendar_id: value,
                                          };
                                          newSettings[settingIndex] = {
                                            ...newSettings[settingIndex],
                                            calendar_export_ids:
                                              calendarExportIds,
                                          };

                                          return {
                                            settings: newSettings,
                                          };
                                        });
                                      }}
                                      value={exportId.calendar_id}
                                      options={options}
                                      placeholder="選択してください"
                                    />
                                    <ButtonText
                                      labelClassNameContainer="text-[#D32932] !border-0"
                                      label="削除"
                                      onClick={() => {
                                        void formik.setValues((prev) => {
                                          const newSettings = [
                                            ...prev.settings,
                                          ];
                                          let calendarExportIds = newSettings[
                                            settingIndex
                                          ].calendar_export_ids?.filter(
                                            (_item, index) =>
                                              index !== exportIndex,
                                          );
                                          if (calendarExportIds?.length === 0) {
                                            calendarExportIds = [
                                              EmptyCalendarEvent,
                                            ];
                                          }
                                          newSettings[settingIndex] = {
                                            ...newSettings[settingIndex],
                                            calendar_export_ids:
                                              calendarExportIds,
                                          };

                                          return {
                                            settings: newSettings,
                                          };
                                        });
                                      }}
                                    />
                                  </div>
                                );
                              },
                            )}
                          </div>
                          {/* <OutlineButton
                              type="button"
                              color="primary"
                              onClick={() => {
                                void formik.setValues((prev) => {
                                  const newSettings = [...prev.settings];
                                  const calendarExportIds = [
                                    ...(newSettings[settingIndex]
                                      .calendar_export_ids || []),
                                    { calendar_id: "" },
                                  ];
                                  newSettings[settingIndex] = {
                                    ...newSettings[settingIndex],
                                    calendar_export_ids: calendarExportIds,
                                  };

                                  return {
                                    settings: newSettings,
                                  };
                                });
                              }}
                              customClass="text-base !font-bold leading-normal"
                            >
                              追加
                            </OutlineButton> */}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="flex justify-between">
              <OutlineButton
                type="button"
                color="primary"
                onClick={addNewSetting}
                customClass="text-base !font-bold leading-normal"
              >
                対象コンテンツの追加
              </OutlineButton>
            </div>
          </div>
        )}
      </article>
      {configDetail?.gcalendar_email && (
        <div className="flex gap-x-2 justify-end bg-[#4C4C4C] px-6 py-2 w-[100vw] fixed bottom-0 left-0 z-[11]">
          <Button
            type="submit"
            label={"保存"}
            size="sm"
            className="w-40"
            onClick={() => formik.handleSubmit()}
          />
        </div>
      )}
    </div>
  );
};

AccountSyncGoogleCalendarPage.getLayout = function getLayout(
  children: ReactElement,
) {
  return <LayoutTemplate>{children}</LayoutTemplate>;
};

export default AccountSyncGoogleCalendarPage;
